'use client';

import { useState, KeyboardEvent } from 'react';
import { useInternalNotesContext } from 'src/shared/components/internal-notes/InternalNotesContext';
import {
  useUpdateInternalNote,
  useDeleteInternalNote,
} from 'src/shared/queries/internal-notes/useInternalNotesMutations';
import { InternalNote } from 'src/shared/types/internalNote.types';
import { relativeTime } from 'src/shared/lib/utils';
import ConfirmDeleteDialog from 'src/shared/components/ui/modals/ConfirmDeleteModal';
import TripleDotActions from 'src/shared/components/ui/dropdowns/triple-dot-actions';

type NoteItemProps = {
  note: InternalNote;
};

export default function NoteItem({ note }: NoteItemProps) {
  const { integrationId, threadId, currentUserId } = useInternalNotesContext();

  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(note.content);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const updateMutation = useUpdateInternalNote(integrationId, threadId);
  const deleteMutation = useDeleteInternalNote(integrationId, threadId);

  const isAuthor = note.author?.id === currentUserId;

  const handleSaveEdit = () => {
    const trimmed = editContent.trim();
    if (!trimmed || trimmed === note.content) {
      setEditContent(note.content);
      setIsEditing(false);
      return;
    }
    updateMutation.mutate({ noteId: note.id, content: trimmed }, { onSuccess: () => setIsEditing(false) });
  };

  const handleEditKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSaveEdit();
    }
    if (event.key === 'Escape') {
      setEditContent(note.content);
      setIsEditing(false);
    }
  };

  const authorInitial = note.author?.name?.[0]?.toUpperCase() ?? note.author?.email?.[0]?.toUpperCase() ?? '?';
  const authorEmail = note.author?.email ?? 'Unknown';

  return (
    <div className="group relative bg-white border border-border rounded-[3px] p-3 overflow-hidden">
      {isEditing ? (
        <textarea
          value={editContent}
          onChange={(event) => setEditContent(event.target.value)}
          onKeyDown={handleEditKeyDown}
          onBlur={handleSaveEdit}
          className="w-full text-sm text-typography-black leading-5 border border-border rounded-[3px] px-2 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-neutral-400"
          rows={3}
          autoFocus
        />
      ) : (
        <p
          className={`text-sm text-typography-black leading-5 whitespace-pre-wrap break-words max-h-[120px] overflow-y-auto ${isAuthor ? 'pr-7' : ''}`}
        >
          {note.content}
        </p>
      )}

      <div className="flex items-center gap-1.5 mt-3">
        <div className="shrink-0 w-5 h-5 rounded-full bg-dashboardblue-600 flex items-center justify-center">
          <span className="text-xs font-semibold text-white leading-4">{authorInitial}</span>
        </div>
        <span className="flex-1 min-w-0 text-sm text-typography-darkgrey truncate leading-5">{authorEmail}</span>
        <span className="shrink-0 text-xs text-typography-grey leading-4">{relativeTime(note.createdAt)}</span>
      </div>

      {isAuthor && !isEditing && (
        <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <TripleDotActions
            onEdit={() => {
              setEditContent(note.content);
              setIsEditing(true);
            }}
            onDelete={() => setShowDeleteConfirm(true)}
          />
        </div>
      )}

      <ConfirmDeleteDialog
        target="this note"
        open={showDeleteConfirm}
        loading={deleteMutation.isPending}
        onOpenChange={setShowDeleteConfirm}
        onDelete={() => deleteMutation.mutate(note.id)}
      />
    </div>
  );
}
